import { useState, useEffect, useRef } from 'react';
import Kendra from 'aws-sdk/clients/kendra';
import { AvailableFacetManager } from '../search/facets/AvailableFacetManager';
import { SelectedFacetManager } from '../search/facets/SelectedFacetManager';
import {
    DataSourceNameLookup,
    getAttributeTypeLookup,
    getDataSourceNameLookup,
    IndexFieldNameToDocumentAttributeValueType,
} from '../search/facets/utils';
import { QueryResultType, PAGE_SIZE } from '../constants';
import { useApi } from 'src/hooks/useApi';
import { useAuth } from 'src/components/providers/AuthProvider';
import {
    publishKendraQuery,
    publishKendraNoResults,
    publishKendraError,
} from '../kendraMetrics';

interface UseKendraSearchProps {
    initialQuery?: string;
    userLanguage?: string;
    facetConfiguration?: {
        facetsToShowWhenUncollapsed: number;
        showCount: boolean;
        updateAvailableFacetsWhenFilterChange: boolean;
        facetPanelDefaultOpen: boolean;
    };
}

export const useKendraSearch = ({
    initialQuery,
    userLanguage,
    facetConfiguration,
}: UseKendraSearchProps) => {
    const { user } = useAuth();
    const lastPublishedQuery = useRef<string>('');
    const [dataReady, setDataReady] = useState(false);
    const [searchResults, setSearchResults] = useState<Kendra.QueryResult>({});
    const [docResults, setDocResults] = useState<Kendra.QueryResultItemList>(
        []
    );
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const [queryText, setQueryText] = useState(initialQuery || '');
    const [error, setError] = useState<Error | undefined>(undefined);
    const [attributeTypeLookup, setAttributeTypeLookup] = useState<
        IndexFieldNameToDocumentAttributeValueType | undefined
    >(undefined);
    const [availableFacets, setAvailableFacets] = useState(
        AvailableFacetManager.empty()
    );
    const [selectedFacets, setSelectedFacets] = useState(
        SelectedFacetManager.empty()
    );
    const [index, setIndex] = useState<
        Kendra.DescribeIndexResponse | undefined
    >(undefined);
    const [facetsOpen, setFacetsOpen] = useState(
        (facetConfiguration && facetConfiguration.facetPanelDefaultOpen) ??
            false
    );
    const [dataSourceNameLookup, setDataSourceNameLookup] = useState<
        DataSourceNameLookup | undefined
    >(undefined);
    const [maxPageWithResults, setMaxPageWithResults] = useState(1);
    const [totalDocumentCount, setTotalDocumentCount] = useState(0);

    const queryApi = useApi({ url: '/kendra/query', method: 'POST' }, true);
    const indexApi = useApi({ url: '/kendra/index', method: 'GET' });
    const dataSourcesApi = useApi({
        url: '/kendra/data-sources',
        method: 'GET',
    });

    useEffect(() => {
        indexApi.makeRequest().then((response) => {
            setAttributeTypeLookup(getAttributeTypeLookup(response?.data));
            setIndex(response?.data);
        });
    }, []);

    useEffect(() => {
        if (!index) return;

        dataSourcesApi.makeRequest().then((response) => {
            setDataSourceNameLookup(getDataSourceNameLookup(response?.data));
        });
    }, [index]);

    useEffect(() => {
        if (initialQuery?.length) {
            getResults(initialQuery, 1);
        }
    }, [initialQuery]);

    const getResultsHelper = (
        queryText: string,
        pageNumber: number,
        filter?: Kendra.AttributeFilter
    ) => {
        setDataReady(false);

        let combinedFilter = filter;
        if (userLanguage) {
            const languageMap: { [key: string]: string } = {
                'English (US)': 'English',
                'French (Canada)': 'French',
                'French (France)': 'French',
                German: 'German',
                Italian: 'Italian',
                Japanese: 'Japanese',
                Polish: 'Polish',
                Spanish: 'Spanish',
                Czech: 'Czech',
                Arabic: 'Arabic',
            };

            const kendraLanguage = languageMap[userLanguage] || userLanguage;

            const languageFilter: Kendra.AttributeFilter = {
                ContainsAny: {
                    Key: 'languages',
                    Value: { StringListValue: [kendraLanguage] },
                },
            };

            if (filter) {
                combinedFilter = {
                    AndAllFilters: [filter, languageFilter],
                };
            } else {
                combinedFilter = languageFilter;
            }
        }

        queryApi
            .makeRequest({
                data: {
                    queryText,
                    pageNumber,
                    attributeFilter: combinedFilter,
                },
            })
            .then((response) => {
                const results = response?.data;
                const documentResults: Kendra.QueryResultItemList = [];

                if (
                    user?.hashedUserId &&
                    lastPublishedQuery.current !== queryText
                ) {
                    publishKendraQuery(user.hashedUserId, queryText);
                    lastPublishedQuery.current = queryText;
                }

                if (results && results.ResultItems) {
                    results.ResultItems.forEach(
                        (result: Kendra.QueryResultItem) => {
                            switch (result.Type) {
                                case QueryResultType.Document:
                                    documentResults.push(result);
                                    break;
                                default:
                                    break;
                            }
                        }
                    );

                    if (documentResults.length === 0 && user?.hashedUserId) {
                        publishKendraNoResults(user.hashedUserId, queryText);
                    }

                    if (
                        !filter ||
                        (filter &&
                            facetConfiguration?.updateAvailableFacetsWhenFilterChange)
                    ) {
                        setAvailableFacets(
                            AvailableFacetManager.fromQueryResult(results)
                        );
                    }

                    const maxResults = Math.min(
                        results.TotalNumberOfResults || 0,
                        100
                    );
                    const maxPages = Math.ceil(maxResults / PAGE_SIZE);

                    setMaxPageWithResults(maxPages);
                    setTotalDocumentCount(maxResults);
                    setSearchResults(results);
                    setDataReady(true);
                    setDocResults(documentResults);
                    setError(undefined);
                } else {
                    setSearchResults({});
                    setDocResults(documentResults);
                    setDataReady(true);
                    setError(undefined);
                }

                setCurrentPageNumber(pageNumber);
                setQueryText(queryText);
            })
            .catch((e) => {
                if (user?.hashedUserId) {
                    publishKendraError(
                        user.hashedUserId,
                        queryText,
                        e.message || 'Unknown error'
                    );
                }
                setSearchResults({});
                setDataReady(true);
                setError(e);
            });
    };

    const getResults = (queryText: string, pageNumber: number = 1) => {
        setSelectedFacets(selectedFacets.clearAll());
        setMaxPageWithResults(1);
        setTotalDocumentCount(0);
        getResultsHelper(queryText, pageNumber);
    };

    const computeFilterAndReSubmitQuery = (
        facetsToUse: SelectedFacetManager,
        queryText: string,
        pageNumber: number = 1
    ) => {
        const filter = facetsToUse.buildAttributeFilter(attributeTypeLookup);
        getResultsHelper(queryText, pageNumber, filter);
    };

    const onSelectedFacetsChanged = (
        updatedSelectedFacets: SelectedFacetManager
    ) => {
        setSelectedFacets(updatedSelectedFacets);
        computeFilterAndReSubmitQuery(updatedSelectedFacets, queryText);
    };

    const handleClickExpander = () => {
        setFacetsOpen(!facetsOpen);
    };

    const handlePageChange = (queryText: string, pageNumber: number) => {
        const filter = selectedFacets.buildAttributeFilter(attributeTypeLookup);
        getResultsHelper(queryText, pageNumber, filter);
    };

    return {
        // State
        dataReady,
        searchResults,
        docResults,
        currentPageNumber,
        queryText,
        error,
        attributeTypeLookup,
        availableFacets,
        selectedFacets,
        index,
        facetsOpen,
        dataSourceNameLookup,
        maxPageWithResults,
        totalDocumentCount,
        // Actions
        getResults,
        onSelectedFacetsChanged,
        handleClickExpander,
        handlePageChange,
        setSelectedFacets,
    };
};
